import { forwardRef, useState, useEffect, useCallback, useRef } from 'react';
import Barcode from 'react-barcode';
import QRCode from 'react-qr-code';
import { HiOutlineArrowLeft } from 'react-icons/hi';
import { IoNotificationsOutline } from 'react-icons/io5';
import { FiUpload } from 'react-icons/fi';
import { HiX } from 'react-icons/hi';

import { Modal, ModalMethods, toasts } from '../../../../ui-component';
import { api } from '../../../../utils/apiRequest';
import LocationPickerModal from './LocationPickerModal';
import LabelConfigModal from './LabelConfigModal';
import BarcodeScannerModal from './BarcodeScannerModal';

export type Variant = {
  id: number;
  name: string;
  readyMadeOutfitId: number;
  variantAttributes: Record<string, string>;
  sku: string;
  barcode: string;
  defaultCodeType: 'QR' | 'BARCODE' | null;
  existingCodeType: 'QR_CODE' | 'BARCODE' | null;
  existingCodeValue: string | null;
  price: number;
  sellingPrice: number;
  costPrice: number;
  stock: number;
  unit: string;
  lowStockAlert: number;
  storageBinId: number | null;
  storageBinName: string | null;
  storageLocation: string | null;
  imageRefIds: string[] | null;
  imageUrls: string[] | null;
};

type ModifyVariantModalProps = {
  variant: Variant | null;
  itemName: string;
  onSaved: () => void;
};

type FormData = {
  sku: string;
  barcode: string;
  price: number;
  cost_price: number;
  stock: number;
  low_stock_alert: number;
  storage_location: string;
  image_ref_ids: string[];
  existing_code_type: 'QR_CODE' | 'BARCODE' | null;
  existing_code_value: string;
};

const ModifyVariantModal = forwardRef<ModalMethods, ModifyVariantModalProps>(
  ({ variant, itemName, onSaved }, ref) => {
    const [formData, setFormData] = useState<FormData>({
      sku: '',
      barcode: '',
      price: 0,
      cost_price: 0,
      stock: 0,
      low_stock_alert: 0,
      storage_location: '',
      image_ref_ids: [],
      existing_code_type: null,
      existing_code_value: '',
    });
    const [isSaving, setIsSaving] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [codeType, setCodeType] = useState<'QR' | 'BARCODE'>('BARCODE');
    const [storageBinId, setStorageBinId] = useState<number | null>(null);
    const [uploadedImages, setUploadedImages] = useState<{ url: string; refId: string }[]>([]);
    const [activeImageIdx, setActiveImageIdx] = useState(0);
    const locationPickerRef = useRef<ModalMethods>(null);
    const labelConfigRef = useRef<ModalMethods>(null);
    const barcodeScannerRef = useRef<ModalMethods>(null);

    useEffect(() => {
      if (variant) {
        setFormData({
          sku: variant.sku || '',
          barcode: variant.barcode || '',
          price: variant.sellingPrice || 0,
          cost_price: variant.costPrice || 0,
          stock: variant.stock || 0,
          low_stock_alert: variant.lowStockAlert || 0,
          storage_location: variant.storageLocation || '',
          image_ref_ids: variant.imageRefIds || [],
          existing_code_type: variant.existingCodeType || null,
          existing_code_value: variant.existingCodeValue || '',
        });
        setStorageBinId(variant.storageBinId || null);
        setCodeType(variant.defaultCodeType || 'BARCODE');
        const urls = variant.imageUrls || [];
        const refIds = variant.imageRefIds || [];
        setUploadedImages(urls.map((url, idx) => ({ url, refId: refIds[idx] || '' })));
        setActiveImageIdx(0);
      }
    }, [variant]);

    const handleFieldChange = useCallback(
      (field: keyof FormData, value: string | number | string[] | null) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
      },
      []
    );

    const handleNumberChange = useCallback((field: keyof FormData, value: string) => {
      const parsed = value === '' ? 0 : parseFloat(value);

      if (!isNaN(parsed)) {
        setFormData((prev) => ({ ...prev, [field]: parsed }));
      }
    }, []);

    const handleSave = async () => {
      if (!variant) {
        return;
      }

      if (!formData.sku.trim()) {
        toasts('error', 'SKU is required', 'variant-sku-required');

        return;
      }

      try {
        setIsSaving(true);

        const existingType = formData.existing_code_type || null;
        const existingVal = formData.existing_code_value || null;

        const payload: Record<string, unknown> = {
          sku: formData.sku,
          barcode: formData.barcode,
          defaultCodeType: codeType,
          default_code_type: codeType,
          existingCodeType: existingType,
          existing_code_type: existingType,
          existingCodeValue: existingVal,
          existing_code_value: existingVal,
          price: formData.price,
          cost_price: formData.cost_price,
          stock: formData.stock,
          low_stock_alert: formData.low_stock_alert,
          storage_location: formData.storage_location,
          image_ref_ids: formData.image_ref_ids,
        };

        if (storageBinId) {
          payload.storage_bin_id = storageBinId;
        }

        const response = await api.putRequest(`readymade/variant/${variant.id}`, payload);

        if (response.status) {
          if (typeof ref === 'object' && ref !== null && 'current' in ref && ref.current) {
            ref.current.hide();
          }

          onSaved();
        } else {
          toasts('error', response.message || 'Failed to update variant', 'variant-update-error');
        }
      } catch (err) {
        if (err instanceof Error) {
          toasts('error', err.message, 'variant-update-error');
        }
      } finally {
        setIsSaving(false);
      }
    };

    const handleClose = () => {
      if (typeof ref === 'object' && ref !== null && 'current' in ref && ref.current) {
        ref.current.hide();
      }
    };

    const handleRemoveImage = useCallback(
      (idx: number) => {
        const updated = uploadedImages.filter((_, i) => i !== idx);

        setUploadedImages(updated);
        handleFieldChange(
          'image_ref_ids',
          updated.map((img) => img.refId)
        );

        if (activeImageIdx >= updated.length && updated.length > 0) {
          setActiveImageIdx(updated.length - 1);
        } else if (updated.length === 0) {
          setActiveImageIdx(0);
        }
      },
      [uploadedImages, activeImageIdx, handleFieldChange]
    );

    const handleFileUpload = useCallback(
      async (files: FileList) => {
        const formDataUpload = new FormData();

        Array.from(files).forEach((file) => {
          formDataUpload.append('file', file);
        });
        formDataUpload.append('file_type', '11');

        setIsUploading(true);

        try {
          const response = await api.postRequest(
            'storage/upload_multiple_files',
            formDataUpload,
            true
          );

          if (response.status && response.data?.response?.length > 0) {
            const newImages = response.data.response.map(
              (img: { reference_id: string; short_lived_url: string; image_link?: string }) => ({
                url: img.image_link || img.short_lived_url,
                refId: img.reference_id,
              })
            );
            const updated = [...uploadedImages, ...newImages].slice(0, 8);

            setUploadedImages(updated);
            handleFieldChange(
              'image_ref_ids',
              updated.map((img) => img.refId)
            );

            if (uploadedImages.length === 0 && updated.length > 0) {
              setActiveImageIdx(0);
            }
          } else {
            toasts('error', response.message || 'Failed to upload images', 'image-upload-error');
          }
        } catch (err) {
          if (err instanceof Error) {
            toasts('error', err.message, 'image-upload-error');
          }
        } finally {
          setIsUploading(false);
        }
      },
      [uploadedImages, handleFieldChange]
    );

    const unitLabel = variant?.unit?.toLowerCase() || 'pieces';

    const barcodeValue = formData.barcode.trim();
    const hasBarcode = barcodeValue.length > 0;
    const isQrCode = codeType === 'QR';

    const existingValue = formData.existing_code_value.trim();
    const hasExistingCode = existingValue.length > 0;
    const isExistingQr = formData.existing_code_type === 'QR_CODE';

    const headerComponent = (
      <div className="flex items-center justify-between w-full">
        <button
          type="button"
          onClick={handleClose}
          className="flex items-center gap-2 text-gray-700 hover:text-gray-900 transition-colors"
        >
          <HiOutlineArrowLeft size={20} />
          <span className="font-inter font-medium text-sm">Back</span>
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={isSaving}
          className="px-6 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 transition-colors disabled:opacity-60"
        >
          {isSaving ? 'Saving...' : 'Save'}
        </button>
      </div>
    );

    return (
      <Modal
        ref={ref}
        size="xl"
        headerComponent={headerComponent}
        showFooter={false}
        showCloseIcon={false}
      >
        <div className="px-2 py-4">
          <div className="flex gap-8 mb-8">
            <div>
              <p className="text-xs text-gray-500 font-inter mb-1">Variant Name</p>
              <p className="font-inter font-bold text-xl text-gray-900">{variant?.name || '—'}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 font-inter mb-1">Item Name</p>
              <p className="font-inter font-bold text-xl text-gray-900">{itemName || '—'}</p>
            </div>
          </div>

          <div className="flex items-start gap-4 mb-8 flex-wrap">
            <div className="flex items-center gap-2">
              <div>
                <p className="text-xs text-gray-500 font-inter mb-1">Quantity</p>
                <span className="inline-flex items-center px-3 py-2 bg-gray-100 rounded-lg text-sm font-medium text-gray-700 border border-gray-200">
                  {unitLabel}
                </span>
              </div>
            </div>

            <div className="flex-1 min-w-[140px]">
              <p className="text-xs text-gray-500 font-inter mb-1">&nbsp;</p>
              <div className="border border-gray-200 rounded-lg px-3 py-2 bg-white">
                <input
                  type="text"
                  value={formData.sku}
                  onChange={(e) => handleFieldChange('sku', e.target.value)}
                  className="w-full outline-none text-sm font-inter font-medium text-gray-800"
                  placeholder="SKU Code"
                />
              </div>
            </div>

            <div className="min-w-[90px]">
              <p className="text-xs text-gray-500 font-inter mb-1">Quantity</p>
              <div className="border border-gray-200 rounded-lg px-3 py-2 bg-white">
                <input
                  type="number"
                  value={formData.stock || ''}
                  onChange={(e) => handleNumberChange('stock', e.target.value)}
                  className="w-full outline-none text-sm font-inter text-gray-800"
                  placeholder="0"
                />
              </div>
            </div>

            <div className="min-w-[90px]">
              <p className="text-xs text-gray-500 font-inter mb-1">Min Level</p>
              <div className="border border-gray-200 rounded-lg px-3 py-2 bg-white flex items-center gap-2">
                <input
                  type="number"
                  value={formData.low_stock_alert || ''}
                  onChange={(e) => handleNumberChange('low_stock_alert', e.target.value)}
                  className="w-full outline-none text-sm font-inter text-gray-800"
                  placeholder="0"
                />
                <IoNotificationsOutline size={16} className="text-gray-400 flex-shrink-0" />
              </div>
            </div>

            <div className="min-w-[120px]">
              <p className="text-xs text-gray-500 font-inter mb-1">Cost Price</p>
              <div className="border border-gray-200 rounded-lg px-3 py-2 bg-white flex items-center gap-2">
                <span className="text-sm text-gray-500">₹</span>
                <input
                  type="number"
                  value={formData.cost_price || ''}
                  onChange={(e) => handleNumberChange('cost_price', e.target.value)}
                  className="w-full outline-none text-sm font-inter text-gray-800"
                  placeholder="0"
                />
                <IoNotificationsOutline size={16} className="text-gray-400 flex-shrink-0" />
              </div>
            </div>

            <div className="min-w-[120px]">
              <p className="text-xs text-gray-500 font-inter mb-1">Selling Price</p>
              <div className="border border-gray-200 rounded-lg px-3 py-2 bg-white flex items-center gap-2">
                <span className="text-sm text-gray-500">₹</span>
                <input
                  type="number"
                  value={formData.price || ''}
                  onChange={(e) => handleNumberChange('price', e.target.value)}
                  className="w-full outline-none text-sm font-inter text-gray-800"
                  placeholder="0"
                />
                <IoNotificationsOutline size={16} className="text-gray-400 flex-shrink-0" />
              </div>
            </div>
          </div>

          <div className="mb-8">
            <p className="text-xs text-gray-500 font-inter mb-1">Location</p>
            <div className="flex items-center gap-2">
              <div
                className="inline-flex items-center px-4 py-2.5 border border-gray-200 rounded-lg bg-white cursor-pointer hover:border-gray-400 transition-colors"
                onClick={() => locationPickerRef.current?.show()}
              >
                <span
                  className={`text-sm font-inter font-medium ${
                    formData.storage_location ? 'text-gray-800' : 'text-gray-400'
                  }`}
                >
                  {formData.storage_location || 'Select location'}
                </span>
              </div>
              {formData.storage_location && (
                <button
                  type="button"
                  onClick={() => {
                    handleFieldChange('storage_location', '');
                    setStorageBinId(null);
                  }}
                  className="text-xs text-gray-500 hover:text-gray-700 font-inter"
                >
                  Clear
                </button>
              )}
            </div>
          </div>

          <div className="flex gap-6">
            <div className="flex-1 border border-gray-200 rounded-2xl p-5 bg-white">
              <div className="flex items-center justify-between mb-4">
                <p className="font-inter font-bold text-base text-gray-900">Product Images</p>
                <label
                  className={`px-3 py-1.5 border border-gray-300 rounded-lg text-xs font-medium text-gray-700 flex items-center gap-1.5 hover:bg-gray-50 transition-colors ${
                    isUploading ? 'opacity-50 pointer-events-none' : 'cursor-pointer'
                  }`}
                >
                  <FiUpload size={12} />
                  <span>{isUploading ? 'Uploading...' : 'Upload Image'}</span>
                  <input
                    type="file"
                    accept="image/jpeg,image/png"
                    multiple
                    className="hidden"
                    disabled={isUploading}
                    onChange={(e) => {
                      if (e.target.files && e.target.files.length > 0) {
                        handleFileUpload(e.target.files);
                      }

                      e.target.value = '';
                    }}
                  />
                </label>
              </div>

              {uploadedImages.length === 0 ? (
                <div className="bg-gray-100 rounded-xl p-8 text-center">
                  <p className="font-inter font-semibold text-sm text-gray-700 mb-1">
                    Enhance Visibility with great images
                  </p>
                  <p className="text-xs text-gray-500">
                    Upload upto 8 images, with a total limit of 30 MB.
                    <br />
                    Supported forma JPG and PNG
                  </p>
                </div>
              ) : (
                <>
                  <div className="relative w-full aspect-[4/3] bg-gray-100 rounded-xl overflow-hidden mb-3">
                    {uploadedImages[activeImageIdx]?.url ? (
                      <img
                        src={uploadedImages[activeImageIdx].url}
                        alt={uploadedImages[activeImageIdx].url}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <span className="text-sm text-gray-400 font-inter">
                          {uploadedImages[activeImageIdx]?.refId || 'No preview'}
                        </span>
                      </div>
                    )}

                    <button
                      type="button"
                      onClick={() => handleRemoveImage(activeImageIdx)}
                      className="absolute top-2 right-2 w-6 h-6 bg-black/50 text-white rounded-full flex items-center justify-center hover:bg-black/70 transition-colors"
                    >
                      <HiX size={14} />
                    </button>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setActiveImageIdx((p) => Math.max(0, p - 1))}
                      disabled={activeImageIdx === 0}
                      className="w-7 h-7 border border-gray-300 rounded flex items-center justify-center text-gray-500 hover:bg-gray-50 disabled:opacity-30 flex-shrink-0"
                    >
                      ‹
                    </button>
                    <div className="flex gap-1.5 overflow-x-auto">
                      {uploadedImages.map((img, idx) => (
                        <button
                          key={idx}
                          type="button"
                          onClick={() => setActiveImageIdx(idx)}
                          className={`w-12 h-12 rounded-lg flex-shrink-0 overflow-hidden border-2 transition-all ${
                            idx === activeImageIdx
                              ? 'border-blue-500'
                              : 'border-transparent hover:border-gray-300'
                          }`}
                        >
                          {img.url ? (
                            <img
                              src={img.url}
                              alt={img.refId}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                              <span className="text-[8px] text-gray-400 truncate px-0.5">
                                {img.refId}
                              </span>
                            </div>
                          )}
                        </button>
                      ))}
                    </div>
                    <button
                      type="button"
                      onClick={() =>
                        setActiveImageIdx((p) => Math.min(uploadedImages.length - 1, p + 1))
                      }
                      disabled={activeImageIdx >= uploadedImages.length - 1}
                      className="w-7 h-7 border border-gray-300 rounded flex items-center justify-center text-gray-500 hover:bg-gray-50 disabled:opacity-30 flex-shrink-0"
                    >
                      ›
                    </button>
                  </div>
                </>
              )}
            </div>

            <div className="w-[280px] flex-shrink-0 border border-gray-200 rounded-2xl p-5 bg-white">
              <div className="flex items-center justify-between mb-4">
                <p className="font-inter font-bold text-base text-gray-900">QR / Barcodes</p>
                <div className="flex rounded-lg border border-gray-200 overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setCodeType('QR')}
                    className={`flex items-center justify-center w-9 h-9 transition-colors ${
                      isQrCode
                        ? 'bg-gray-800 text-white'
                        : 'bg-white text-gray-500 hover:bg-gray-100'
                    }`}
                    aria-label="QR code"
                  >
                    <svg width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden>
                      <rect x="2" y="2" width="4" height="4" fill="currentColor" />
                      <rect x="8" y="2" width="2" height="4" fill="currentColor" />
                      <rect x="12" y="2" width="4" height="4" fill="currentColor" />
                      <rect x="2" y="8" width="4" height="2" fill="currentColor" />
                      <rect x="8" y="8" width="4" height="2" fill="currentColor" />
                      <rect x="14" y="8" width="2" height="2" fill="currentColor" />
                      <rect x="2" y="12" width="4" height="4" fill="currentColor" />
                      <rect x="8" y="14" width="2" height="2" fill="currentColor" />
                      <rect x="12" y="12" width="4" height="4" fill="currentColor" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    onClick={() => setCodeType('BARCODE')}
                    className={`flex items-center justify-center w-9 h-9 border-l border-gray-200 transition-colors ${
                      !isQrCode
                        ? 'bg-gray-800 text-white'
                        : 'bg-white text-gray-500 hover:bg-gray-100'
                    }`}
                    aria-label="Barcode"
                  >
                    <svg width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden>
                      <rect x="2" y="4" width="1.5" height="12" fill="currentColor" />
                      <rect x="5" y="4" width="1" height="12" fill="currentColor" />
                      <rect x="7.5" y="4" width="2" height="12" fill="currentColor" />
                      <rect x="11" y="4" width="1" height="12" fill="currentColor" />
                      <rect x="13.5" y="4" width="1.5" height="12" fill="currentColor" />
                      <rect x="16.5" y="4" width="1" height="12" fill="currentColor" />
                    </svg>
                  </button>
                </div>
              </div>

              <div className="space-y-3">
                {hasBarcode && (
                  <div className="relative rounded-lg bg-white border border-[#d1d1d1] px-3 pb-4 pt-6">
                    <button
                      type="button"
                      onClick={() => handleFieldChange('barcode', '')}
                      className="absolute hidden top-0 right-0 z-10  h-8 w-8 -translate-y-1/2 translate-x-1/2 items-center justify-center rounded-full bg-red-500 text-white shadow-md hover:bg-red-600 transition-colors"
                      aria-label={isQrCode ? 'Remove QR code' : 'Remove barcode'}
                    >
                      <HiX size={18} />
                    </button>

                    <div className="flex flex-col items-center">
                      <p className="mb-3 text-center text-xs font-inter text-gray-600">
                        Created by{' '}
                        <span className="font-bold uppercase tracking-wide text-[#4b7bec]">
                          DARZEE
                        </span>
                      </p>

                      {isQrCode ? (
                        <div className="flex items-center justify-center p-2">
                          <QRCode value={barcodeValue} size={120} level="M" />
                        </div>
                      ) : (
                        <div className="w-full overflow-hidden px-0.5 [&_svg]:block [&_svg]:h-auto [&_svg]:max-h-[56px] [&_svg]:w-full [&_svg]:max-w-full">
                          <Barcode
                            value={barcodeValue}
                            format="CODE128"
                            width={2.4}
                            height={56}
                            displayValue={false}
                            margin={0}
                            marginLeft={4}
                            marginRight={4}
                            background="#ffffff"
                            lineColor="#000000"
                          />
                        </div>
                      )}

                      <p className="mt-3 text-center text-xs font-inter tracking-wide text-gray-500">
                        {barcodeValue}
                      </p>
                    </div>
                  </div>
                )}

                {hasExistingCode ? (
                  <div className="relative rounded-lg bg-white border border-[#d1d1d1] px-3 pb-4 pt-6">
                    <button
                      type="button"
                      onClick={() => {
                        handleFieldChange('existing_code_value', '');
                        handleFieldChange('existing_code_type', null);
                      }}
                      className="absolute top-0 right-0 z-10 flex h-8 w-8 -translate-y-1/2 translate-x-1/2 items-center justify-center rounded-full bg-red-500 text-white shadow-md hover:bg-red-600 transition-colors"
                      aria-label="Remove existing code"
                    >
                      <HiX size={18} />
                    </button>

                    <div className="flex flex-col items-center">
                      <p className="mb-2 text-center text-xs font-inter text-gray-600 uppercase tracking-wide font-semibold">
                        Linked External Code
                      </p>

                      <div className="flex rounded-md border border-gray-200 overflow-hidden mb-3">
                        <button
                          type="button"
                          onClick={() => handleFieldChange('existing_code_type', 'QR_CODE')}
                          className={`px-3 py-1 text-xs font-inter font-medium transition-colors ${
                            isExistingQr
                              ? 'bg-gray-800 text-white'
                              : 'bg-white text-gray-500 hover:bg-gray-100'
                          }`}
                        >
                          QR
                        </button>
                        <button
                          type="button"
                          onClick={() => handleFieldChange('existing_code_type', 'BARCODE')}
                          className={`px-3 py-1 text-xs font-inter font-medium border-l border-gray-200 transition-colors ${
                            !isExistingQr
                              ? 'bg-gray-800 text-white'
                              : 'bg-white text-gray-500 hover:bg-gray-100'
                          }`}
                        >
                          Barcode
                        </button>
                      </div>

                      {isExistingQr ? (
                        <div className="flex items-center justify-center p-2">
                          <QRCode value={existingValue} size={120} level="M" />
                        </div>
                      ) : (
                        <div className="w-full overflow-hidden px-0.5 [&_svg]:block [&_svg]:h-auto [&_svg]:max-h-[56px] [&_svg]:w-full [&_svg]:max-w-full">
                          <Barcode
                            value={existingValue}
                            format="CODE128"
                            width={2.4}
                            height={56}
                            displayValue={false}
                            margin={0}
                            marginLeft={4}
                            marginRight={4}
                            background="#ffffff"
                            lineColor="#000000"
                          />
                        </div>
                      )}

                      <p className="mt-3 text-center text-xs font-inter tracking-wide text-gray-500">
                        {existingValue}
                      </p>
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => barcodeScannerRef.current?.show()}
                    className="flex items-center gap-3 w-full p-3 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors text-left"
                  >
                    <div className="w-10 h-10 bg-white/80 rounded flex items-center justify-center flex-shrink-0 border border-gray-200/80">
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden>
                        <rect x="2" y="4" width="1.5" height="12" fill="#666" />
                        <rect x="5" y="4" width="1" height="12" fill="#666" />
                        <rect x="7.5" y="4" width="2" height="12" fill="#666" />
                        <rect x="11" y="4" width="1" height="12" fill="#666" />
                        <rect x="13.5" y="4" width="1.5" height="12" fill="#666" />
                        <rect x="16.5" y="4" width="1" height="12" fill="#666" />
                      </svg>
                    </div>
                    <span className="font-inter font-semibold text-sm text-gray-800 uppercase tracking-wide">
                      Link Existing QR / Barcode
                    </span>
                  </button>
                )}

                <button
                  type="button"
                  onClick={() => labelConfigRef.current?.show()}
                  className="flex items-center gap-3 w-full p-3 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors text-left"
                >
                  <div className="w-10 h-10 bg-white/80 rounded flex items-center justify-center flex-shrink-0 border border-gray-200/80">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden>
                      <path d="M3 6h14v8H3V6z" stroke="#666" strokeWidth="1.2" fill="none" />
                      <rect x="4.5" y="8" width="1" height="4" fill="#666" />
                      <rect x="6.5" y="8" width="1.5" height="4" fill="#666" />
                      <rect x="9" y="8" width="1" height="4" fill="#666" />
                      <rect x="11" y="8" width="2" height="4" fill="#666" />
                      <rect x="13.5" y="8" width="1" height="4" fill="#666" />
                    </svg>
                  </div>
                  <span className="font-inter font-semibold text-sm text-gray-800 uppercase tracking-wide">
                    Print Custom Label
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
        <LocationPickerModal
          ref={locationPickerRef}
          subtitle={variant?.name || 'Variant'}
          onLocationSelect={(location, binId) => {
            handleFieldChange('storage_location', location);
            setStorageBinId(binId);
          }}
        />
        <LabelConfigModal
          ref={labelConfigRef}
          readymadeId={variant?.readyMadeOutfitId || 0}
          variantId={variant?.id || 0}
          itemName={itemName}
          price={variant?.sellingPrice || 0}
          onSaved={onSaved}
        />
        <BarcodeScannerModal
          ref={barcodeScannerRef}
          onScanned={(value) => {
            handleFieldChange('existing_code_value', value);

            if (!formData.existing_code_type) {
              handleFieldChange('existing_code_type', 'BARCODE');
            }
          }}
        />
      </Modal>
    );
  }
);

ModifyVariantModal.displayName = 'ModifyVariantModal';

export default ModifyVariantModal;
